import { Card, Divider, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useDashboard } from "../../../contexts/DashboardContext";
import { ChartSkeleton } from "./Skeletons";
import { DashboardSourceInfo } from "./DashboardSourceInfo";

const StyledPaper = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: 320,
  overflow: "visible",
}));

const ChartTitle = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 500,
});

export const MMsByMonth = () => {
  const { loading, mmsByMonthGeneral, mmsByMonthFiltered, hasFilters } = useDashboard();

  // Labels sempre do general (sem filtro)
  const labels = mmsByMonthGeneral?.labels ?? [];
  // Barras: representam o filtro quando houver filtros aplicados
  const barSeries = hasFilters && mmsByMonthFiltered?.series
    ? mmsByMonthFiltered.series.filter((s: any) => s.type === "bar")
    : [];
  // Inverter ordem: ano anterior primeiro, ano atual depois
  const sortedBarSeries = [...barSeries].sort((a: any, b: any) => {
    // Extrai ano do label ("Ano corrente (2026)", "Ano anterior (2025)")
    const getAno = (label: string) => {
      const match = label.match(/(\d{4})/);
      return match ? parseInt(match[1], 10) : 0;
    };
    return getAno(a.label) - getAno(b.label);
  });

  // Linhas: sempre do geral
  const lineSeries = mmsByMonthGeneral?.series
    ? [...mmsByMonthGeneral.series.filter((s: any) => s.type === "line")].sort(
        (a: any, b: any) => {
          const getAno = (label: string) => {
            const match = label.match(/(\d{4})/);
            return match ? parseInt(match[1], 10) : 0;
          };

          return getAno(a.label) - getAno(b.label);
        }
      )
    : [];

  // Monta um objeto por mês: { month: "jan.", ... }
  const chartData = labels.map((label: string, index: number) => {
    const point: Record<string, any> = { month: label };
    sortedBarSeries.forEach((s: any) => {
      point[s.key] = s.data[index] ?? 0;
    });
    lineSeries.forEach((s: any) => {
      point[s.key] = s.data[index] ?? 0;
    });
    return point;
  });

  // Override legendas: barras como "Filtro" e linhas como "Geral"
  const getBarLabel = (s: any) => {
    const match = s.label.match(/(\d{4})/);
    const year = match ? parseInt(match[1], 10) : null;

    if (year === mmsByMonthFiltered?.previousYear) {
      return `Filtro (${mmsByMonthFiltered.previousYear})`;
    }
    if (year === mmsByMonthFiltered?.currentYear) {
      return `Filtro (${mmsByMonthFiltered.currentYear})`;
    }

    // Fallback defensivo caso o backend mude o formato do label
    return s.label;
  };
  const getLineLabel = (s: any) => {
    // Exemplo: "Geral (2026)" ou "Geral (2025)"
    const match = s.label.match(/(\d{4})/);
    const ano = match ? match[1] : "";
    return ano ? `Geral (${ano})` : "Geral";
  };

  return (
    <StyledPaper>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <ChartTitle>MMs por mês</ChartTitle>
          <DashboardSourceInfo context="MMs por mês (mês da data de início do evento)" />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <ChartSkeleton />
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 20, left: -28, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6B7280", fontSize: 12 }}
              interval={chartData.length > 30 ? "preserveStartEnd" : 0}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6B7280", fontSize: 12 }}
              allowDecimals={false}
            />

            <Tooltip wrapperStyle={{ zIndex: 50 }} />

            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />

            {/* Barras: filtro */}
            {sortedBarSeries.map((s: any) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={getBarLabel(s)}
                fill={s.backgroundColor ?? "rgba(30,111,217,0.55)"}
                radius={[3, 3, 0, 0]}
                maxBarSize={32}
              />
            ))}

            {/* Linhas: geral */}
            {lineSeries.map((s: any) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={getLineLabel(s)}
                stroke={s.borderColor ?? "#1e6fd9"}
                strokeWidth={2}
                strokeDasharray={s.borderDash ? s.borderDash.join(" ") : undefined}
                dot={false}
                activeDot={{ r: 5 }}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </StyledPaper>
  );
};
